const DB_NAME = 'kinetic_sftp_local_vault';
const DB_VERSION = 1;
const STORE_META = 'meta';
const STORE_ENTRIES = 'entries';
const VERIFIER_KEY = 'verifier';
const KDF_KEY = 'kdf';
const VERIFIER_TEXT = 'kinetic-sftp-vault-v1';
const KDF_ITERATIONS = 250000;

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

let unlockedKey = null;

const hasIndexedDb = typeof indexedDB !== 'undefined';
const hasCrypto = typeof crypto !== 'undefined' && !!crypto?.subtle;

const toBase64 = (bytes) => {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
};

const fromBase64 = (value) => {
  const decoded = atob(String(value || ''));
  const bytes = new Uint8Array(decoded.length);
  for (let i = 0; i < decoded.length; i += 1) {
    bytes[i] = decoded.charCodeAt(i);
  }
  return bytes;
};

const requestToPromise = (request) =>
new Promise((resolve, reject) => {
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error('IndexedDB request failed.'));
});

const openDb = () =>
new Promise((resolve, reject) => {
  if (!hasIndexedDb) {
    reject(new Error('IndexedDB is not available in this browser.'));
    return;
  }
  const request = indexedDB.open(DB_NAME, DB_VERSION);
  request.onupgradeneeded = () => {
    const db = request.result;
    if (!db.objectStoreNames.contains(STORE_META)) {
      db.createObjectStore(STORE_META);
    }
    if (!db.objectStoreNames.contains(STORE_ENTRIES)) {
      db.createObjectStore(STORE_ENTRIES, { keyPath: 'name' });
    }
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error('Unable to open IndexedDB.'));
});

const withStore = async (storeName, mode, worker) => {
  const db = await openDb();
  try {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    const result = await worker(store);
    await new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error || new Error('IndexedDB transaction failed.'));
      tx.onabort = () => reject(tx.error || new Error('IndexedDB transaction aborted.'));
    });
    return result;
  } finally {
    db.close();
  }
};

const randomBytes = (length) => {
  const out = new Uint8Array(length);
  crypto.getRandomValues(out);
  return out;
};

const deriveKey = async (password, kdfMeta, usages = ['encrypt', 'decrypt']) => {
  const baseKey = await crypto.subtle.importKey(
    'raw',
    textEncoder.encode(String(password || '')),
    'PBKDF2',
    false,
    ['deriveKey']
  );
  const salt = fromBase64(kdfMeta.salt);
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: Number(kdfMeta.iterations) || KDF_ITERATIONS,
      hash: 'SHA-256'
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    usages
  );
};

const encryptString = async (plainText, key) => {
  const iv = randomBytes(12);
  const cipherBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    textEncoder.encode(String(plainText || ''))
  );
  return {
    iv: toBase64(iv),
    data: toBase64(new Uint8Array(cipherBuffer))
  };
};

const decryptString = async (cipher, key) => {
  const iv = fromBase64(cipher?.iv || '');
  const data = fromBase64(cipher?.data || '');
  const plainBuffer = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data);
  return textDecoder.decode(plainBuffer);
};

const getMeta = async (key) =>
withStore(STORE_META, 'readonly', async (store) => requestToPromise(store.get(key)));

const setMeta = async (key, value) =>
withStore(STORE_META, 'readwrite', async (store) => {
  await requestToPromise(store.put(value, key));
});

const getEntry = async (name) =>
withStore(STORE_ENTRIES, 'readonly', async (store) => requestToPromise(store.get(name)));

const listEntries = async () =>
withStore(STORE_ENTRIES, 'readonly', async (store) => {
  const result = await requestToPromise(store.getAll());
  return Array.isArray(result) ? result : [];
});

const putEntry = async (entry) =>
withStore(STORE_ENTRIES, 'readwrite', async (store) => {
  await requestToPromise(store.put(entry));
});

const removeEntry = async (name) =>
withStore(STORE_ENTRIES, 'readwrite', async (store) => {
  await requestToPromise(store.delete(name));
});

const normalizeProtocol = (protocol) => protocol === 'ftps' ? 'ftps' : 'sftp';

const normalizeEntry = (entry) => {
  const protocol = normalizeProtocol(entry?.protocol);
  return {
    name: String(entry?.name || '').trim(),
    description: String(entry?.description || ''),
    host: String(entry?.host || ''),
    port: Number(entry?.port) || (protocol === 'ftps' ? 21 : 2022),
    username: String(entry?.username || ''),
    password: String(entry?.password || ''),
    protocol
  };
};

const ensureSupported = () => {
  if (!hasIndexedDb || !hasCrypto) {
    throw new Error('Local encrypted storage is not available in this browser.');
  }
};

const getKdfMeta = async () => {
  const kdf = await getMeta(KDF_KEY);
  if (!kdf || !kdf.salt) return null;
  return {
    salt: String(kdf.salt),
    iterations: Number(kdf.iterations) || KDF_ITERATIONS
  };
};

export const localVault = {
  isSupported() {
    return hasIndexedDb && hasCrypto;
  },

  async status() {
    if (!this.isSupported()) {
      return { hasMaster: false, unlocked: false };
    }
    const kdf = await getKdfMeta();
    const verifier = await getMeta(VERIFIER_KEY);
    return {
      hasMaster: Boolean(kdf && verifier),
      unlocked: Boolean(unlockedKey)
    };
  },

  async setMaster(password) {
    ensureSupported();
    const secret = String(password || '');
    if (!secret) {
      return { ok: false, error: 'Master password is required.' };
    }
    const kdfMeta = {
      salt: toBase64(randomBytes(16)),
      iterations: KDF_ITERATIONS
    };
    const key = await deriveKey(secret, kdfMeta);
    const verifier = await encryptString(VERIFIER_TEXT, key);
    await setMeta(KDF_KEY, kdfMeta);
    await setMeta(VERIFIER_KEY, verifier);
    unlockedKey = key;
    return { ok: true };
  },

  async unlock(password) {
    ensureSupported();
    const kdfMeta = await getKdfMeta();
    const verifier = await getMeta(VERIFIER_KEY);
    if (!kdfMeta || !verifier) {
      return { ok: false, error: 'No master password set.' };
    }
    try {
      const key = await deriveKey(String(password || ''), kdfMeta);
      const plain = await decryptString(verifier, key);
      if (plain !== VERIFIER_TEXT) {
        return { ok: false, error: 'Invalid master password.' };
      }
      unlockedKey = key;
      return { ok: true };
    } catch {
      return { ok: false, error: 'Invalid master password.' };
    }
  },

  async list() {
    ensureSupported();
    const entries = await listEntries();
    return entries.
    map((entry) => ({
      name: entry.name,
      description: entry.description || '',
      host: entry.host,
      port: entry.port,
      username: entry.username,
      protocol: entry.protocol || 'sftp',
      encrypted: Boolean(entry.encrypted)
    })).
    sort((a, b) => a.name.localeCompare(b.name));
  },

  async get(name) {
    ensureSupported();
    const safeName = String(name || '').trim();
    if (!safeName) throw new Error('Missing login name.');
    const entry = await getEntry(safeName);
    if (!entry) throw new Error('Saved login not found.');
    if (entry.encrypted) {
      if (!unlockedKey) throw new Error('Vault locked');
      const password = await decryptString(entry.passwordCipher, unlockedKey);
      return {
        name: entry.name,
        description: entry.description || '',
        host: entry.host,
        port: entry.port,
        username: entry.username,
        password,
        protocol: entry.protocol || 'sftp',
        encrypted: true
      };
    }
    return {
      name: entry.name,
      description: entry.description || '',
      host: entry.host,
      port: entry.port,
      username: entry.username,
      password: String(entry.password || ''),
      protocol: entry.protocol || 'sftp',
      encrypted: false
    };
  },

  async save(entry, encrypt = false) {
    ensureSupported();
    const normalized = normalizeEntry(entry);
    if (!normalized.name) throw new Error('Missing entry name.');
    const payload = {
      name: normalized.name,
      description: normalized.description,
      host: normalized.host,
      port: normalized.port,
      username: normalized.username,
      protocol: normalized.protocol,
      encrypted: Boolean(encrypt)
    };
    if (payload.encrypted) {
      if (!unlockedKey) throw new Error('Vault locked');
      payload.passwordCipher = await encryptString(normalized.password, unlockedKey);
    } else {
      payload.password = normalized.password;
    }
    await putEntry(payload);
    return { ok: true };
  },

  async delete(name) {
    ensureSupported();
    const safeName = String(name || '').trim();
    if (!safeName) return { ok: true };
    await removeEntry(safeName);
    return { ok: true };
  }
};
